<template>
  <div class="app-pagination">
    <div class="app-pagination__info">
      <span class="app-pagination__showing">Showing {{ startItem }}-{{ endItem }} of {{ total }}</span>
      <span class="app-pagination__rows">
        <span class="app-pagination__rows-label">Rows per page</span>
        <el-select
          class="app-pagination__size-select"
          size="mini"
          :value="pageSize"
          @change="onSizeChange"
        >
          <el-option v-for="size in pageSizes" :key="size" :label="size" :value="size"></el-option>
        </el-select>
      </span>
    </div>
    <div class="app-pagination__pager">
      <button
        type="button"
        class="app-pagination__nav"
        :disabled="currentPage <= 1"
        @click="onCurrentChange(currentPage - 1)"
      >&laquo;</button>
      <button
        v-for="item in pagerItems"
        :key="item.key"
        type="button"
        class="app-pagination__page"
        :class="{ 'is-active': !item.ellipsis && item.page === currentPage, 'is-ellipsis': item.ellipsis }"
        :disabled="item.ellipsis"
        @click="!item.ellipsis && onCurrentChange(item.page)"
      >{{ item.ellipsis ? '…' : item.page }}</button>
      <button
        type="button"
        class="app-pagination__nav"
        :disabled="currentPage >= pageCount"
        @click="onCurrentChange(currentPage + 1)"
      >&raquo;</button>
    </div>
  </div>
</template>

<script>
// Drop-in visual replacement for <el-pagination>: same total/page-size/current-page props and
// size-change/current-change events, so pages only need to swap the tag (see the CMS table pages
// for usage) - not a v-model API change.
export default {
  name: "AppPagination",
  props: {
    total: { type: Number, default: 0 },
    pageSize: { type: Number, default: 10 },
    currentPage: { type: Number, default: 1 },
    pageSizes: { type: Array, default: () => [10, 20, 30, 40] }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    startItem() {
      return this.total === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.pageSize, this.total);
    },
    // Shows up to BOUNDARY_COUNT pages from whichever end the current page is near (or 1 page +
    // 1 sibling on each side of the current page in the middle), collapsing any gap into a single
    // non-clickable ellipsis - e.g. "1 2 3 4 5 6 7 8 9 10 ... 4976" near the start, or
    // "1 ... 49 50 51 ... 4976" in the middle.
    pagerItems() {
      const pageCount = this.pageCount;
      const current = this.currentPage;
      const boundaryCount = 10;
      const items = [];
      const addPage = page => items.push({ key: `p${page}`, page });
      const addEllipsis = key => items.push({ key, ellipsis: true });

      if (pageCount <= boundaryCount + 3) {
        for (let p = 1; p <= pageCount; p++) addPage(p);
        return items;
      }

      if (current <= boundaryCount) {
        for (let p = 1; p <= boundaryCount; p++) addPage(p);
        addEllipsis("end-ellipsis");
        addPage(pageCount);
      } else if (current > pageCount - boundaryCount) {
        addPage(1);
        addEllipsis("start-ellipsis");
        for (let p = pageCount - boundaryCount + 1; p <= pageCount; p++) addPage(p);
      } else {
        addPage(1);
        addEllipsis("start-ellipsis");
        addPage(current - 1);
        addPage(current);
        addPage(current + 1);
        addEllipsis("end-ellipsis");
        addPage(pageCount);
      }
      return items;
    }
  },
  methods: {
    onSizeChange(value) {
      this.$emit("size-change", value);
    },
    onCurrentChange(page) {
      if (page < 1 || page > this.pageCount || page === this.currentPage) {
        return;
      }
      this.$emit("current-change", page);
    }
  }
};
</script>

<style scoped>
.app-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 12px;
  margin-top: 20px;
  padding: 14px 20px;
  background: #fff;
  border: 1px solid #e6ebee;
  border-radius: 8px;
}
.app-pagination__info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 20px;
  row-gap: 8px;
  color: #003448;
  font-size: 14px;
}
.app-pagination__rows {
  display: flex;
  align-items: center;
  column-gap: 8px;
}
.app-pagination__rows-label {
  white-space: nowrap;
}
.app-pagination__size-select {
  width: 76px;
}
.app-pagination__pager {
  display: flex;
  align-items: center;
}
.app-pagination__nav {
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #8a9aa0;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
}
.app-pagination__nav:hover:not(:disabled) {
  background: #eef2f3;
  color: #003448;
}
.app-pagination__nav:disabled {
  color: #d3d9db;
  cursor: not-allowed;
}
.app-pagination__page {
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #003448;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
}
.app-pagination__page:hover:not(.is-active):not(.is-ellipsis) {
  background: #eef2f3;
}
.app-pagination__page.is-active {
  background: #003448;
  color: #fff;
  font-weight: 700;
}
.app-pagination__page.is-ellipsis {
  background: transparent;
  cursor: default;
  color: #8a9aa0;
}
</style>



// WEBPACK FOOTER //
// src/components/common/AppPagination.vue